import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

interface TokenContextType {
  idToken: string | null;
  tokenReceivedAt: number | null;
  setIdToken: (token: string | null) => void;
  isAuthenticated: boolean;
  clearToken: () => void;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

interface TokenProviderProps {
  children: ReactNode;
}

export const TokenProvider: React.FC<TokenProviderProps> = ({ children }) => {
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const [tokenReceivedAt, setTokenReceivedAtState] = useState<number | null>(null);

  // Extract idToken from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get("idToken");

    if (tokenFromUrl) {
      const now = Date.now();
      setIdTokenState(tokenFromUrl);
      setTokenReceivedAtState(now);
    }
  }, []);

  const setIdToken = (token: string | null) => {
    setIdTokenState(token);
    setTokenReceivedAtState(token ? Date.now() : null);
  };

  const clearToken = () => {
    setIdTokenState(null);
    setTokenReceivedAtState(null);
  };

  const isAuthenticated = !!idToken;

  return (
    <TokenContext.Provider
      value={{
        idToken,
        tokenReceivedAt,
        setIdToken,
        isAuthenticated,
        clearToken,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export const useToken = (): TokenContextType => {
  const context = useContext(TokenContext);
  if (context === undefined) {
    throw new Error("useToken must be used within a TokenProvider");
  }
  return context;
};
